// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Stargate modem cache/signal payloads store LTE metrics as unit strings
 * ("-75 dBm", "No LTE Connection"), not numbers. The dashboard must parse
 * those before rendering — treating any string as N/A is why connected
 * EC25s showed empty RSSI/RSRP/RSRQ/SINR.
 *
 * Device firmware: stargate-container-ui server-rs modem_cache.rs /
 * routes_modem.rs. Do not treat GET /api/modem/signal as a live radio read;
 * it is the same 120s cache wrapped in `{ data }`.
 */

export function isSentinel(trimmed) {
  const lower = trimmed.toLowerCase();
  if (lower === 'n/a' || lower === '—' || lower === '-' || lower === 'unknown' || lower === 'no service') {
    return true;
  }
  return /^no lte\b/i.test(trimmed);
}

const RANGES = {
  RSSI: { min: -125, max: -40 },
  RSRP: { min: -140, max: -40 },
  RSRQ: { min: -30, max: 0 },
  SINR: { min: -20, max: 40 },
};

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function pickKeyed(obj, keys) {
  if (!isPlainObject(obj)) return undefined;
  for (const key of keys) {
    if (obj[key] != null) return obj[key];
  }
  const lower = {};
  for (const [k, v] of Object.entries(obj)) lower[k.toLowerCase()] = v;
  for (const key of keys) {
    const hit = lower[String(key).toLowerCase()];
    if (hit != null) return hit;
  }
  return undefined;
}

function hasMetricKeys(obj) {
  if (!isPlainObject(obj)) return false;
  return ['rssi', 'rsrp', 'rsrq', 'sinr'].some((k) => pickKeyed(obj, [k]) != null);
}

/**
 * Parse a firmware signal field into a finite number, or null.
 * Accepts numbers and leading signed floats in strings ("-75 dBm").
 */
export function parseSignalMetric(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || isSentinel(trimmed)) return null;
  const match = trimmed.match(/^(-?\d+(?:\.\d+)?)/);
  if (!match) return null;
  const n = Number.parseFloat(match[1]);
  return Number.isFinite(n) ? n : null;
}

function inRange(n, min, max) {
  return n != null && n >= min && n <= max;
}

function normalizeRssi(raw) {
  let n = parseSignalMetric(raw);
  if (n == null) return null;
  if (n >= 0 && n <= 31 && Number.isInteger(n)) n = -113 + 2 * n;
  return inRange(n, RANGES.RSSI.min, RANGES.RSSI.max) ? n : null;
}

function normalizeTyped(raw, type) {
  const n = parseSignalMetric(raw);
  if (n == null) return null;
  const range = RANGES[type];
  return inRange(n, range.min, range.max) ? n : null;
}

function unwrapSection(obj) {
  if (!isPlainObject(obj)) return {};
  if (isPlainObject(obj.data) && !hasMetricKeys(obj) && (hasMetricKeys(obj.data) || obj.data.model || obj.data.imei || obj.data.type || obj.data.status)) {
    return obj.data;
  }
  return obj;
}

/**
 * Locate the signal object without eating a full cache `{ info, signal, network }`.
 * Order: signal.data → signal → data (if metric keys) → signal_strength → self.
 */
export function pickSignalObject(raw) {
  if (!isPlainObject(raw)) return {};
  if (isPlainObject(raw.signal)) {
    if (isPlainObject(raw.signal.data) && hasMetricKeys(raw.signal.data)) return raw.signal.data;
    if (hasMetricKeys(raw.signal)) return raw.signal;
  }
  if (isPlainObject(raw.data)) {
    if (hasMetricKeys(raw.data)) return raw.data;
    if (isPlainObject(raw.data.signal) || isPlainObject(raw.data.info)) {
      return pickSignalObject(raw.data);
    }
  }
  if (isPlainObject(raw.signal_strength)) {
    const inner = unwrapSection(raw.signal_strength);
    if (hasMetricKeys(inner)) return inner;
  }
  if (hasMetricKeys(raw)) return raw;
  return {};
}

function qualityFromStatus(status) {
  if (status == null) return null;
  const s = String(status).trim();
  if (!s || isSentinel(s)) return null;
  return s;
}

export function normalizeModemSignal(raw) {
  const src = pickSignalObject(raw);
  return {
    rssi: normalizeRssi(pickKeyed(src, ['rssi'])),
    rsrp: normalizeTyped(pickKeyed(src, ['rsrp']), 'RSRP'),
    rsrq: normalizeTyped(pickKeyed(src, ['rsrq']), 'RSRQ'),
    sinr: normalizeTyped(pickKeyed(src, ['sinr']), 'SINR'),
    signal_bars: parseSignalMetric(pickKeyed(src, ['signal_bars'])) ?? 0,
    signal_quality: qualityFromStatus(pickKeyed(src, ['signal_quality', 'status'])),
  };
}

export function normalizeModemCache(cachedData) {
  const root =
    isPlainObject(cachedData?.data) &&
    (cachedData.data.info || cachedData.data.signal || cachedData.data.network)
      ? cachedData.data
      : isPlainObject(cachedData)
        ? cachedData
        : {};
  return {
    info: unwrapSection(root.info || root.modem_info),
    signal: normalizeModemSignal(root),
    network: unwrapSection(root.network || root.network_info),
  };
}

/** True when we have a usable RSSI or an RSRP-shaped RSRP (not RSRQ dumped into rsrp). */
export function hasLteSignal(signal) {
  if (!signal) return false;
  if (signal.rssi != null && Number.isFinite(signal.rssi)) return true;
  return signal.rsrp != null && inRange(signal.rsrp, RANGES.RSRP.min, RANGES.RSRP.max);
}

export function overallSignalPercent(signal) {
  if (signal?.rssi != null && Number.isFinite(signal.rssi)) {
    return Math.min(100, Math.max(0, ((signal.rssi + 113) / 63) * 100));
  }
  if (signal?.rsrp != null && inRange(signal.rsrp, RANGES.RSRP.min, RANGES.RSRP.max)) {
    return Math.min(100, Math.max(0, ((signal.rsrp + 140) / 96) * 100));
  }
  return 0;
}

export function overallMetric(signal) {
  if (signal?.rssi != null) return { value: signal.rssi, type: 'RSSI' };
  if (signal?.rsrp != null) return { value: signal.rsrp, type: 'RSRP' };
  return { value: null, type: null };
}

function nonEmpty(value, ...rejected) {
  const s = value == null ? '' : String(value).trim();
  if (!s) return '';
  if (rejected.some((r) => s.toLowerCase() === String(r).toLowerCase())) return '';
  return s;
}

function combinedStatus(...parts) {
  return parts
    .filter((p) => p != null && String(p).trim())
    .join(' ')
    .toLowerCase();
}

function statusImpliesDown(status) {
  return /\bdisconnect/.test(status);
}

function statusImpliesUp(status) {
  if (statusImpliesDown(status)) return false;
  return /\bconnected\b/.test(status) || /\bregistered\b/.test(status);
}

export function isLteBearerUp({ info, network, operator, networkType, cellId, signal } = {}) {
  const status = combinedStatus(info?.status, network?.status, signal?.signal_quality);
  if (statusImpliesDown(status)) return false;
  if (statusImpliesUp(status)) return true;
  const op = nonEmpty(operator, '—', '-', 'No Service', 'Unknown');
  if (op) return true;
  const nt = (networkType || '').toLowerCase();
  if (nt && nt !== 'unknown' && !nt.includes('no service') && !nt.startsWith('no ')) return true;
  const cell = nonEmpty(cellId, 'Unknown', '—', '-');
  return Boolean(cell);
}

export function registrationDisplay(status) {
  const raw = status == null ? '' : String(status).trim();
  if (!raw) return { label: 'Unknown', tone: 'unknown' };
  const lower = raw.toLowerCase();
  const label = raw.charAt(0).toUpperCase() + raw.slice(1);
  if (statusImpliesDown(lower)) return { label, tone: 'unknown' };
  if (lower.includes('denied')) return { label, tone: 'denied' };
  if (/\bsearch/.test(lower) || /\bconnecting\b/.test(lower)) return { label, tone: 'searching' };
  if (statusImpliesUp(lower)) return { label, tone: 'ok' };
  return { label, tone: 'unknown' };
}

function headerLabel({ hasSignal, bearerUp, signal }) {
  if (hasSignal) return signal.signal_quality || 'Connected';
  if (bearerUp) return signal.signal_quality || 'Connected';
  return 'No LTE';
}

function overallQualityLabel(hasSignal, bearerUp, percent, signal) {
  if (hasSignal) {
    if (percent >= 60) return 'Strong';
    if (percent >= 30) return 'Fair';
    return 'Weak';
  }
  if (bearerUp) return signal?.signal_quality || 'Connected';
  return 'No Signal';
}

/**
 * Single view-model so cache-key picks and registration cannot drift from tests.
 */
export function buildModemViewModel({
  cachedData,
  liveInfo,
  liveSignal,
  liveNetwork,
  cacheOk,
} = {}) {
  let info;
  let signal;
  let network;
  if (cacheOk && isPlainObject(cachedData)) {
    ({ info, signal, network } = normalizeModemCache(cachedData));
  } else {
    info = unwrapSection(liveInfo);
    signal = normalizeModemSignal(liveSignal);
    network = unwrapSection(liveNetwork);
  }

  const operator = nonEmpty(info.operator || network.operator, '—', '-') || 'No Service';
  const networkType =
    String(network.type || network.network_type || 'Unknown')
      .replace(/[\r\n]+.*$/, '')
      .trim() || 'Unknown';
  const band = network.band || 'Unknown';
  const cellId = network.cell_id || 'Unknown';
  const status = info.status || network.status || signal.signal_quality || '';
  const hasSignal = hasLteSignal(signal);
  const bearerUp = isLteBearerUp({
    info,
    network,
    operator,
    networkType,
    cellId,
    signal,
  });
  const overallPercent = hasSignal ? overallSignalPercent(signal) : 0;

  return {
    parsedModel: info.model || 'Unknown',
    parsedImei: info.imei || 'Unknown',
    status,
    signal,
    operator,
    network_type: networkType,
    band,
    cell_id: cellId,
    registration: registrationDisplay(status),
    hasLteSignal: hasSignal,
    bearerUp,
    headerLabel: headerLabel({ hasSignal, bearerUp, signal }),
    overallPercent,
    overallQualityLabel: overallQualityLabel(hasSignal, bearerUp, overallPercent, signal),
    overallMetric: overallMetric(signal),
  };
}
